import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Button,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import axiosClient from "../api/axiosClient";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../features/auth/authSlice";
import { useNavigate } from "react-router-dom";

export default function ManageCompanyPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const storedUser = JSON.parse(localStorage.getItem("user"));
  const canEdit = Boolean(storedUser?.can_edit);
  const companyId = storedUser?.company?.id || storedUser?.company_id;

  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const [form, setForm] = useState({
    // Office & Emails
    office_direct_line_mobile: "",
    office_email_address: "",
    personal_email_address: "",

    // Office numbers & website
    office_number_1: "",
    office_number_2: "",
    website: "",

    // Accounts Contact
    accounts_contact: "",
    accounts_contact_first_name: "",
    accounts_contact_last_name: "",
    accounts_contact_email_address: "",

    // General Contact
    general_contact: "",
    general_contact_email_address: "",

    // Company Address
    street_1: "",
    street_2: "",
    street_3: "",
    city: "",
    country: "",
    post_code: "",

    // Technologies & Certification
    technologies: "",
    certification_body: "",

    // Insurance
    public_liability_insurance_provider: "",
    public_liability_insurance_policy_number: "",
    workmanship_warranty_provider: "",
    deposit_protection_insurance_provider: "",
  });

  const showSnack = (msg, severity = "success") => setSnack({ open: true, msg, severity });
  const handleChange = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  useEffect(() => {
    const load = async () => {
      if (!companyId) {
        showSnack("No company linked to your account.", "warning");
        setLoading(false);
        return;
      }
      try {
        // Pull fresh company from backend (fallback to local if API unavailable)
        const res = await axiosClient.get(`/companies/${companyId}`);
        const c = res.data || storedUser?.company || {};
        setForm({
          office_direct_line_mobile: c.office_direct_line_mobile ?? "",
          office_email_address: c.office_email_address ?? c.email ?? "",
          personal_email_address: c.personal_email_address ?? "",

          office_number_1: c.office_number_1 ?? c.telephone1 ?? "",
          office_number_2: c.office_number_2 ?? c.telephone2 ?? "",
          website: c.website ?? "",

          accounts_contact: c.accounts_contact ?? "",
          accounts_contact_first_name: c.accounts_contact_first_name ?? "",
          accounts_contact_last_name: c.accounts_contact_last_name ?? "",
          accounts_contact_email_address: c.accounts_contact_email_address ?? "",

          general_contact: c.general_contact ?? "",
          general_contact_email_address: c.general_contact_email_address ?? "",

          street_1: c.street_1 ?? "",
          street_2: c.street_2 ?? "",
          street_3: c.street_3 ?? "",
          city: c.city ?? "",
          country: c.country ?? "",
          post_code: c.post_code ?? c.postcode ?? "",

          technologies: Array.isArray(c.technologies) ? c.technologies.join(", ") : c.technologies ?? "",
          certification_body: c.certification_body ?? "",

          public_liability_insurance_provider: c.public_liability_insurance_provider ?? "",
          public_liability_insurance_policy_number: c.public_liability_insurance_policy_number ?? "",
          workmanship_warranty_provider: c.workmanship_warranty_provider ?? "",
          deposit_protection_insurance_provider: c.deposit_protection_insurance_provider ?? "",
        });
      } catch (e) {
        console.error(e);
        const c = storedUser?.company || {};
        setForm((prev) => ({
          ...prev,
          office_direct_line_mobile: c.office_direct_line_mobile ?? "",
          office_email_address: c.office_email_address ?? c.email ?? "",
          personal_email_address: c.personal_email_address ?? "",
          office_number_1: c.office_number_1 ?? c.telephone1 ?? "",
          office_number_2: c.office_number_2 ?? c.telephone2 ?? "",
          website: c.website ?? "",
          accounts_contact: c.accounts_contact ?? "",
          accounts_contact_first_name: c.accounts_contact_first_name ?? "",
          accounts_contact_last_name: c.accounts_contact_last_name ?? "",
          accounts_contact_email_address: c.accounts_contact_email_address ?? "",
          general_contact: c.general_contact ?? "",
          general_contact_email_address: c.general_contact_email_address ?? "",
          street_1: c.street_1 ?? "",
          street_2: c.street_2 ?? "",
          street_3: c.street_3 ?? "",
          city: c.city ?? "",
          country: c.country ?? "",
          post_code: c.post_code ?? c.postcode ?? "",
          technologies: Array.isArray(c.technologies) ? c.technologies.join(", ") : c.technologies ?? "",
          certification_body: c.certification_body ?? "",
          public_liability_insurance_provider: c.public_liability_insurance_provider ?? "",
          public_liability_insurance_policy_number: c.public_liability_insurance_policy_number ?? "",
          workmanship_warranty_provider: c.workmanship_warranty_provider ?? "",
          deposit_protection_insurance_provider: c.deposit_protection_insurance_provider ?? "",
        }));
        showSnack("Loaded local company data (API unavailable).", "info");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const saveCompany = async () => {
    if (!canEdit) return;
    if (!companyId) {
      showSnack("No company linked to your account.", "warning");
      return;
    }
    try {
      const payload = {
        // Office & Emails
        office_direct_line_mobile: form.office_direct_line_mobile,
        office_email_address: form.office_email_address,
        personal_email_address: form.personal_email_address,
        email: form.office_email_address || undefined,

        // Office numbers & website
        office_number_1: form.office_number_1,
        office_number_2: form.office_number_2,
        telephone1: form.office_number_1 || undefined,
        telephone2: form.office_number_2 || undefined,
        website: form.website,

        // Accounts
        accounts_contact: form.accounts_contact,
        accounts_contact_first_name: form.accounts_contact_first_name,
        accounts_contact_last_name: form.accounts_contact_last_name,
        accounts_contact_email_address: form.accounts_contact_email_address,

        // General
        general_contact: form.general_contact,
        general_contact_email_address: form.general_contact_email_address,

        // Address
        street_1: form.street_1,
        street_2: form.street_2,
        street_3: form.street_3,
        city: form.city,
        country: form.country,
        post_code: form.post_code,
        postcode: form.post_code,

        // Tech / certification
        technologies: form.technologies,
        certification_body: form.certification_body,

        // Insurance
        public_liability_insurance_provider: form.public_liability_insurance_provider,
        public_liability_insurance_policy_number: form.public_liability_insurance_policy_number,
        workmanship_warranty_provider: form.workmanship_warranty_provider,
        deposit_protection_insurance_provider: form.deposit_protection_insurance_provider,
      };

      const res = await axiosClient.patch(`/companies/${companyId}`, payload);

      // Update user.company in local storage / redux
      const updatedCompany = res.data;
      const updatedUser = { ...storedUser, company: updatedCompany };
      localStorage.setItem("user", JSON.stringify(updatedUser));
      dispatch(loginSuccess({ user: updatedUser }));

      showSnack("Company updated successfully");
    } catch (err) {
      console.error(err);
      showSnack(err.response?.data?.message ?? "Failed to update company", "error");
    }
  };

  const renderField = (label, value, field) => (
    <Grid item xs={12} sm={6}>
      <TextField
        label={label}
        value={value}
        onChange={handleChange(field)}
        fullWidth
        disabled={!canEdit || loading}
      />
    </Grid>
  );

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#a4b7d7", pb: 8 }}>
      <Container maxWidth="md" sx={{ pt: 6 }}>
        <Card sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
          <CardActions sx={{ justifyContent: "space-between", pb: 0 }}>
            <Typography variant="h5" sx={{ color: "#005695" }}>
              Manage Company
            </Typography>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
          </CardActions>
          <CardContent>
            {/* Office & Emails */}
            <Typography variant="h6" sx={{ color: "#005695", mt: 1, mb: 1 }}>
              Office & Emails
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {renderField("Office Mobile", form.office_direct_line_mobile, "office_direct_line_mobile")}
              {renderField("Office Email", form.office_email_address, "office_email_address")}
              {renderField("Personal Email", form.personal_email_address, "personal_email_address")}
              {renderField("Office Number 1", form.office_number_1, "office_number_1")}
              {renderField("Office Number 2", form.office_number_2, "office_number_2")}
              {renderField("Website", form.website, "website")}
            </Grid>

            {/* Accounts Contact */}
            <Typography variant="h6" sx={{ color: "#005695", mt: 4, mb: 1 }}>
              Accounts Contact
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {renderField("Accounts Contact", form.accounts_contact, "accounts_contact")}
              {renderField("First Name", form.accounts_contact_first_name, "accounts_contact_first_name")}
              {renderField("Last Name", form.accounts_contact_last_name, "accounts_contact_last_name")}
              {renderField("Email", form.accounts_contact_email_address, "accounts_contact_email_address")}
            </Grid>

            {/* General Contact */}
            <Typography variant="h6" sx={{ color: "#005695", mt: 4, mb: 1 }}>
              General Contact
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {renderField("General Contact", form.general_contact, "general_contact")}
              {renderField("Email", form.general_contact_email_address, "general_contact_email_address")}
            </Grid>

            {/* Company Address */}
            <Typography variant="h6" sx={{ color: "#005695", mt: 4, mb: 1 }}>
              Company Address
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {renderField("Street #1", form.street_1, "street_1")}
              {renderField("Street #2", form.street_2, "street_2")}
              {renderField("Street #3", form.street_3, "street_3")}
              {renderField("City", form.city, "city")}
              {renderField("Country", form.country, "country")}
              {renderField("Post Code", form.post_code, "post_code")}
            </Grid>

            {/* Technologies & Certification */}
            <Typography variant="h6" sx={{ color: "#005695", mt: 4, mb: 1 }}>
              Technologies & Certification
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {renderField("Technologies (comma-separated)", form.technologies, "technologies")}
              {renderField("Certification Body", form.certification_body, "certification_body")}
            </Grid>

            {/* Insurance Details */}
            <Typography variant="h6" sx={{ color: "#005695", mt: 4, mb: 1 }}>
              Insurance Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {renderField("Public Liability Provider", form.public_liability_insurance_provider, "public_liability_insurance_provider")}
              {renderField("Public Liability Policy #", form.public_liability_insurance_policy_number, "public_liability_insurance_policy_number")}
              {renderField("Workmanship Warranty Provider", form.workmanship_warranty_provider, "workmanship_warranty_provider")}
              {renderField("Deposit Protection Insurance", form.deposit_protection_insurance_provider, "deposit_protection_insurance_provider")}
            </Grid>
          </CardContent>

          <CardActions sx={{ justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#005695" }}
              disabled={!canEdit || loading}
              onClick={saveCompany}
            >
              Save Company
            </Button>
          </CardActions>
        </Card>
      </Container>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack((s) => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
